/* THEME-UNIFY: Accordion behavior + footer desktop layout polish
   - Product page: desktop uses inline <details> accordion with ONE default panel open.
                 mobile uses the existing bottom-sheet behavior (gs-accordion-sheet.js), so we keep <details> closed.
   - Footer group: desktop shows columns (not accordion). mobile stays collapsible.
   Scoped to avoid impacting other accordions (header drawers, FAQ sections, etc.).
*/
(() => {
  const mqSheet = window.matchMedia("(max-width: 768px)");
  const mqFooterDesktop = window.matchMedia("(min-width: 750px)");

  const debounce = (fn, wait = 120) => {
    let t;
    return (...args) => {
      window.clearTimeout(t);
      t = window.setTimeout(() => fn(...args), wait);
    };
  };

  function inProductTemplate() {
    const main = document.querySelector("main[data-template]");
    const template = main?.getAttribute("data-template") || "";
    return template.includes("product");
  }

  function pickPreferredDetails(detailsList) {
    const preferredPatterns = [
      /\b(size|fit)\b/i,
      /\b(details|features)\b/i,
      /\b(description)\b/i
    ];

    for (const pattern of preferredPatterns) {
      const match = detailsList.find((d) => {
        const summary = d.querySelector("summary");
        const txt = (summary?.textContent || "").trim();
        return pattern.test(txt);
      });
      if (match) return match;
    }

    return detailsList[0] || null;
  }

  function applyProductAccordionDefaults() {
    if (!inProductTemplate()) return;

    // Limit scope to the main product information section so this doesn't affect
    // other page accordions (e.g., FAQ sections elsewhere).
    const productRoots = document.querySelectorAll(".product-information, [id^='ProductInformation-']");
    if (!productRoots.length) return;

    productRoots.forEach((root) => {
      const accordions = root.querySelectorAll("[data-gs-accordion]");
      accordions.forEach((acc) => {
        const details = Array.from(acc.querySelectorAll("details.details"));
        if (!details.length) return;

        // THEME-UNIFY: Mobile now uses on-page accordions (sheet disabled).
        // Open one default panel on mobile, same as desktop.
        if (mqSheet.matches) {
          const alreadyOpen = details.find((d) => d.open);
          const toOpen = alreadyOpen || pickPreferredDetails(details);
          details.forEach((d) => (d.open = d === toOpen));
          return;
        }

        // Desktop: open one (and only one) default panel, unless user already opened something.
        const alreadyOpen = details.find((d) => d.open);
        const toOpen = alreadyOpen || pickPreferredDetails(details);
        details.forEach((d) => (d.open = d === toOpen));
      });
    });
  }

  function applyFooterDesktopColumns() {
    const footerGroup = document.querySelector(".shopify-section-group-footer-group");
    if (!footerGroup) return;

    const footerAccordions = footerGroup.querySelectorAll("[data-gs-accordion]");
    if (!footerAccordions.length) return;

    const isDesktop = mqFooterDesktop.matches;

    footerAccordions.forEach((acc) => {
      const rows = acc.querySelectorAll("accordion-custom[data-theme-unify-accordion-row]");
      const details = acc.querySelectorAll("details.details");
      const summaries = acc.querySelectorAll("summary.details__header");

      if (isDesktop) {
        // Desktop footer: always-expanded, non-interactive (looks like columns).
        details.forEach((d) => (d.open = true));

        rows.forEach((row) => {
          row.dataset.disableOnDesktop = "true";
        });

        summaries.forEach((s) => {
          s.setAttribute("aria-disabled", "true");
          s.setAttribute("tabindex", "-1");
        });
      } else {
        // Mobile footer: collapsible accordion behavior.
        rows.forEach((row) => {
          delete row.dataset.disableOnDesktop;
        });

        summaries.forEach((s) => {
          s.removeAttribute("aria-disabled");
          s.removeAttribute("tabindex");
        });

        // Default to collapsed on mobile for a cleaner footer.
        details.forEach((d) => (d.open = false));
      }
    });
  }

  const runAll = () => {
    applyProductAccordionDefaults();
    applyFooterDesktopColumns();
  };

  const runAllDebounced = debounce(runAll, 60);

  document.addEventListener("DOMContentLoaded", runAll);

  // Theme editor + dynamic section loading
  document.addEventListener("shopify:section:load", runAllDebounced);
  document.addEventListener("shopify:section:reorder", runAllDebounced);
  document.addEventListener("shopify:section:select", runAllDebounced);

  // Breakpoint changes
  if (mqSheet.addEventListener) mqSheet.addEventListener("change", runAllDebounced);
  if (mqFooterDesktop.addEventListener) mqFooterDesktop.addEventListener("change", runAllDebounced);
  window.addEventListener("resize", runAllDebounced);
})();
